// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Container,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Chat,
  CloudUpload,
  Person,
  Logout,
  Assignment,
  People,
  Psychology,
  Dashboard,
  Refresh,
  Groups,
  Article,
  Spellcheck,
  Tune,
  SmartToy,
  Translate,
  Topic,
  GraphicEq,
  MenuBook,
} from '@mui/icons-material';

// Import components
import ChatInterface from './components/ChatInterface';
import VoiceUpload from './components/VoiceUpload';
import QACriteriaManagement from './components/QACriteriaManagement';
import QADashboard from './components/QADashboard/QADashboard';
import LoginPage from './components/LoginPage';
import NotFoundPage from './components/NotFoundPage';
import ShareVoiceTranscriptionPage from './components/ShareVoiceTranscriptionPage';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import IdleSessionWatcher from './components/IdleSessionWatcher';

// Import User Management components
import { UserManagement } from './components/UserManagement';
import PasswordManagement from './components/UserManagement/PasswordManagement';

// Import Team Management component
import TeamManagement from './components/TeamManagement/TeamManagement';

// Import Nexus Settings components
import NexusSettingsPage from './components/NexusSettings/NexusSettingsPage';
import {
  computeSettingsHubNavItems,
  SETTINGS_CATEGORY_READ_PERMISSION,
} from './config/settingsHubNav';

// Import Model Management components
import ModelManagement from './components/ModelManagement/ModelManagement';
import KnowledgeManagement from './components/KnowledgeManagement/KnowledgeManagement';

// Create theme for Nexus-AI
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#13213c', // Company standard color
      light: '#2a3f5f',
      dark: '#0a1520',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6f00', // Orange for voice/audio theme
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function SettingsCategoryRoute() {
  const { category } = useParams();
  const decoded = category ? decodeURIComponent(category) : '';
  const perm = SETTINGS_CATEGORY_READ_PERMISSION[decoded];
  if (!perm) {
    return <Navigate to="/chat" replace />;
  }
  return (
    <ProtectedRoute requiredPermission={perm}>
      <NexusSettingsPage />
    </ProtectedRoute>
  );
}

function SettingsRedirect() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const hubNavItems = useMemo(
    () => computeSettingsHubNavItems(hasPermission),
    [hasPermission],
  );

  useEffect(() => {
    if (hubNavItems.length > 0) {
      navigate(hubNavItems[0].path, { replace: true });
    } else {
      navigate('/chat', { replace: true });
    }
  }, [hubNavItems, navigate]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
      <CircularProgress />
    </Box>
  );
}

function hubTabMuiIcon(kind) {
  if (kind === 'chat_prompts') {
    return <Psychology />;
  }
  if (kind === 'qa_prompts') {
    return <Article />;
  }
  if (kind === 'word_correction') {
    return <Spellcheck />;
  }
  if (kind === 'qa_context_settings') {
    return <Topic />;
  }
  if (kind === 'translation_glossary') {
    return <Translate />;
  }
  if (kind === 'voice_processing_settings') {
    return <GraphicEq />;
  }
  return <Tune />;
}

// Navigation component with URL routing
function Navigation() {
  const location = useLocation();
  const { user, logout, hasPermission, refreshPermissions } = useAuth();
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);

  const hubNavItems = useMemo(
    () => computeSettingsHubNavItems(hasPermission),
    [hasPermission],
  );

  const getTabValue = () => {
    const paths = [];
    if (hasPermission('CHAT_VIEW')) {
      paths.push('/chat');
    }
    if (hasPermission('VOICE_VIEW')) {
      paths.push('/voice-process');
    }
    if (hasPermission('QA_VIEW')) {
      paths.push('/qa-criteria');
    }
    if (hasPermission('QA_DASHBOARD_READ')) {
      paths.push('/qa-dashboard');
    }
    if (hasPermission('USERS_VIEW')) {
      paths.push('/users');
    }
    if (hasPermission('TEAMS_VIEW')) {
      paths.push('/team-management');
    }
    if (hasPermission('MODEL_READ')) {
      paths.push('/model-management');
    }
    if (hasPermission('KNOWLEDGE_READ')) {
      paths.push('/knowledge-management');
    }
    hubNavItems.forEach((item) => paths.push(item.path));

    const currentPath = location.pathname === '/' ? '/chat' : location.pathname;
    const tabIndex = paths.indexOf(currentPath);
    return tabIndex >= 0 ? tabIndex : 0;
  };

  const handleTabChange = () => {
    // Navigation is handled by Link components in Tab labels
  };

  const handleLogout = () => {
    logout();
  };

  const handleUserChipClick = () => {
    setOpenPasswordDialog(true);
  };

  const handlePasswordChanged = () => {
    setOpenPasswordDialog(false);
    // Optionally show a success message
  };

  return (
    <>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Nexus-AI Platform
          </Typography>
          <Typography variant="body2">
            AI-Powered MINOR Vacations
          </Typography>
        </Toolbar>
      </AppBar>

          {/* Navigation Tabs with User Info */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Tabs
              value={getTabValue()}
              onChange={handleTabChange}
              aria-label="main navigation"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {hasPermission('CHAT_VIEW') && (
                <Tab 
                  icon={<Chat />} 
                  label="Chat Interface" 
                  component={Link}
                  to="/chat"
                  id="simple-tab-0"
                />
              )}
              {hasPermission('VOICE_VIEW') && (
                <Tab 
                  icon={<CloudUpload />} 
                  label="Voice Process" 
                  component={Link}
                  to="/voice-process"
                  id="simple-tab-1"
                />
              )}
              {hasPermission('QA_VIEW') && (
                <Tab 
                  icon={<Assignment />} 
                  label="QA Criteria" 
                  component={Link}
                  to="/qa-criteria"
                  id="simple-tab-2"
                />
              )}
              {hasPermission('QA_DASHBOARD_READ') && (
                <Tab 
                  icon={<Dashboard />} 
                  label="QA Dashboard" 
                  component={Link}
                  to="/qa-dashboard"
                  id="simple-tab-3"
                />
              )}
              {hasPermission('USERS_VIEW') && (
                <Tab 
                  icon={<People />} 
                  label="User Management" 
                  component={Link}
                  to="/users"
                  id="simple-tab-4"
                />
              )}
              {hasPermission('TEAMS_VIEW') && (
                <Tab
                  icon={<Groups />}
                  label="Team Management"
                  component={Link}
                  to="/team-management"
                  id="simple-tab-teams"
                />
              )}
              {hasPermission('MODEL_READ') && (
                <Tab 
                  icon={<SmartToy />} 
                  label="Model Management" 
                  component={Link}
                  to="/model-management"
                  id="simple-tab-6"
                />
              )}
              {hasPermission('KNOWLEDGE_READ') && (
                <Tab
                  icon={<MenuBook />}
                  label="Knowledge"
                  component={Link}
                  to="/knowledge-management"
                  id="simple-tab-knowledge"
                />
              )}
              {hubNavItems.map((item, idx) => (
                <Tab
                  key={item.path}
                  icon={hubTabMuiIcon(item.tabIconKind || item.kind)}
                  label={item.label}
                  component={Link}
                  to={item.path}
                  id={`settings-hub-tab-${idx}`}
                />
              ))}
            </Tabs>
            </Box>
            
            {/* User Info and Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
              {user && (
                <Chip
                  icon={<Person />}
                  label={user.full_name || user.nickname || user.username || 'User'}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={handleUserChipClick}
                  sx={{ 
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    },
                    '& .MuiChip-icon': {
                      color: 'primary.main'
                    }
                  }}
                />
              )}
              <IconButton 
                onClick={() => refreshPermissions()}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'white'
                  }
                }}
                title="Refresh Permissions"
              >
                <Refresh />
              </IconButton>
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'white'
                  }
                }}
                title="Logout"
              >
                <Logout />
              </IconButton>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0,
            overflow: 'auto',
            height: 0
          }}>
            <Container maxWidth="lg" sx={{ 
              mt: 3, 
              mb: 1, 
              flex: 1, 
              minHeight: 0,
              overflow: 'visible'
            }}>
              <Routes>
                <Route path="/" element={<ChatInterface />} />
                <Route path="/chat" element={<ChatInterface />} />
                <Route path="/voice-process" element={<VoiceUpload />} />
                <Route path="/qa-criteria" element={<QACriteriaManagement />} />
                <Route path="/qa-dashboard" element={
                  <ProtectedRoute requiredPermission="QA_DASHBOARD_READ">
                    <QADashboard />
                  </ProtectedRoute>
                } />
                
                {/* User Management Routes */}
                <Route path="/users" element={<UserManagement />} />
                
                {/* Team Management Routes */}
                <Route path="/team-management" element={
                  <ProtectedRoute requiredPermission="TEAMS_VIEW">
                    <TeamManagement />
                  </ProtectedRoute>
                } />
                
                {/* Model Management Routes */}
                <Route path="/model-management" element={<ModelManagement />} />

                <Route
                  path="/knowledge-management"
                  element={(
                    <ProtectedRoute requiredPermission="KNOWLEDGE_READ">
                      <KnowledgeManagement />
                    </ProtectedRoute>
                  )}
                />
                
                {/* Settings hub (granular RBAC per path) */}
                <Route path="/settings" element={<SettingsRedirect />} />
                <Route
                  path="/chat-prompts"
                  element={(
                    <ProtectedRoute requiredPermission="CHAT_PROMPTS_READ">
                      <NexusSettingsPage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/qa-prompts"
                  element={(
                    <ProtectedRoute requiredPermission="QA_PROMPTS_READ">
                      <NexusSettingsPage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/word-correction"
                  element={(
                    <ProtectedRoute requiredPermission="WORD_CORRECTION_READ">
                      <NexusSettingsPage />
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/settings-category/ai_models"
                  element={<Navigate to="/model-management" replace />}
                />
                <Route path="/settings-category/:category" element={<SettingsCategoryRoute />} />
              </Routes>
            </Container>
          </Box>

      {/* Footer */}
      <Footer />

      {/* Password Management Dialog */}
      <PasswordManagement
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        user={user}
        onPasswordChanged={handlePasswordChanged}
        isSelfPasswordChange={true}
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <IdleSessionWatcher />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/share/voice/:token" element={<ShareVoiceTranscriptionPage />} />

            {/* Protected Routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  minHeight: '100vh',
                  height: '100vh',
                  overflow: 'hidden'
                }}>
                  <Navigation />
                </Box>
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
